import { useEffect, useRef, useState } from "react";
import { CalendarDays, MessageSquare, SendHorizontal, X } from "lucide-react";
import { useChat } from "@/hooks/useChat";
import { LINKS } from "@/data/content";

const SUGGESTIONS = ["What services do you offer?", "Tell me about coaching", "I'm looking for a job"];

const linkify = (text) =>
  text.split(/(https?:\/\/[^\s)]+)/g).map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-navy">
        {part.replace(/^https?:\/\//, "")}
      </a>
    ) : (
      part
    )
  );

const Bubble = ({ m, i }) => {
  const booking = m.role === "assistant" && m.content.includes("calendly.com");
  return (
    <div data-testid={`chat-message-${m.role}-${i}`} className={`flex flex-col gap-2 ${m.role === "user" ? "items-end" : "items-start"}`}>
      <p
        className={`max-w-[85%] whitespace-pre-wrap px-3.5 py-2.5 text-sm leading-relaxed ${
          m.role === "user" ? "bg-navy text-white" : "bg-snow text-ink"
        }`}
      >
        {m.content ? linkify(m.content) : <span className="inline-block animate-pulse">…</span>}
      </p>
      {booking && (
        <a
          data-testid={`chat-book-call-${i}`}
          href={LINKS.calendly}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary inline-flex h-10 items-center gap-1.5 px-4 text-sm font-semibold"
        >
          <CalendarDays className="h-4 w-4" /> Book a call
        </a>
      )}
    </div>
  );
};

const ChatPanel = ({ onClose }) => {
  const { messages, streaming, send } = useChat();
  const [input, setInput] = useState("");
  const endRef = useRef(null);

  useEffect(() => endRef.current?.scrollIntoView({ block: "end" }), [messages]);

  const submit = (e) => {
    e.preventDefault();
    send(input);
    setInput("");
  };

  return (
    <div data-testid="chat-panel" className="flex h-[min(34rem,calc(100vh-7rem))] w-[min(24rem,calc(100vw-2.5rem))] flex-col border border-navy/15 bg-white">
      <div className="flex items-center justify-between bg-navy px-4 py-3 text-white">
        <div>
          <p className="font-heading text-base font-semibold leading-tight">PKR Assistant</p>
          <p className="text-xs text-white/70">Ask about recruiting, staffing or coaching</p>
        </div>
        <button data-testid="chat-close-button" aria-label="Close chat" onClick={onClose} className="p-1.5 hover:bg-white/10">
          <X className="h-5 w-5" />
        </button>
      </div>
      <div data-testid="chat-messages" role="log" aria-live="polite" aria-label="Chat messages" className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
        <Bubble i="welcome" m={{ role: "assistant", content: "Hi! I'm PKR's virtual assistant. How can I help with your hiring or leadership goals today?" }} />
        {messages.length === 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {SUGGESTIONS.map((s) => (
              <button key={s} data-testid="chat-suggestion" onClick={() => send(s)} className="border border-navy/20 px-3 py-1.5 text-xs font-medium text-navy hover:bg-snow">
                {s}
              </button>
            ))}
          </div>
        )}
        {messages.map((m, i) => <Bubble key={i} m={m} i={i} />)}
        <div ref={endRef} />
      </div>
      <form onSubmit={submit} className="flex items-center gap-2 border-t border-navy/10 p-3">
        <input
          data-testid="chat-input"
          aria-label="Your question"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Type your question…"
          className="h-11 flex-1 border border-navy/15 bg-snow px-3 text-sm outline-none focus-visible:ring-2 focus-visible:ring-azure/50"
        />
        <button data-testid="chat-send-button" type="submit" disabled={streaming || !input.trim()} aria-label="Send" className="btn-primary flex h-11 w-11 items-center justify-center disabled:opacity-40">
          <SendHorizontal className="h-4 w-4" />
        </button>
      </form>
      <a href={LINKS.calendly} target="_blank" rel="noopener noreferrer" data-testid="chat-calendly-link" className="border-t border-navy/10 bg-snow py-2 text-center text-xs font-medium text-navy hover:bg-mist">
        Prefer to talk? Book a free consultation
      </a>
    </div>
  );
};

export const ChatAssistant = () => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid="chat-assistant" className="fixed bottom-5 right-5 z-40 flex flex-col items-end gap-3">
      {open && <ChatPanel onClose={() => setOpen(false)} />}
      <button
        data-testid="chat-launcher-button"
        aria-label={open ? "Close chat" : "Open chat"}
        onClick={() => setOpen((o) => !o)}
        className="btn-primary flex h-12 items-center gap-2 px-4 text-sm font-semibold"
      >
        {open ? <X className="h-5 w-5" /> : <MessageSquare className="h-5 w-5" />}
        {!open && "Chat with PKR"}
      </button>
    </div>
  );
};
